// src/lib/subscriptions.js



import { supabase } from "../../supabaseClient";



/*

 * ============================================================

 * MONTHLY SUBSCRIPTION SYSTEM

 * ============================================================

 *

 * Important:

 *

 * Referral journey access is NOT checked in this file anymore.

 *

 * Referral + once-off journey access is now handled centrally by:

 *

 *   src/lib/access/paidJourneyAccess.js

 *

 * This file is only responsible for:

 *

 * - monthly subscription access

 * - manually granted unlimited access

 * - subscription checkout

 * - subscription cancellation

 * - billing history

 *

 * This prevents duplicate referral checks and unnecessary

 * network requests during normal learner navigation.

 */



const SUBSCRIPTIONS_FLAG = String(
  process.env.REACT_APP_ENABLE_SUBSCRIPTIONS ?? "true"
)
  .trim()
  .toLowerCase();

export const SUBSCRIPTIONS_ENABLED =
  SUBSCRIPTIONS_FLAG !== "false";



/*

 * ------------------------------------------------------------

 * QUEST SUBSCRIPTION RULE

 * ------------------------------------------------------------

 *

 * This remains for older code that may still use

 * subscription-based quest gating.

 *

 * The current learner journey should use paidJourneyAccess.js.

 */

export function requiresSubscriptionForQuest(

  questId

) {

  const raw =

    String(

      questId || ""

    ).trim();



  if (

    raw === "1" ||

    raw === "Q1" ||

    raw === "quest-1" ||

    raw === "financial-foundations"

  ) {

    return false;

  }



  if (

    raw === "2" ||

    raw === "Q2" ||

    raw === "quest-2"

  ) {

    return true;

  }



  if (

    raw === "3" ||

    raw === "Q3" ||

    raw === "quest-3"

  ) {

    return true;

  }



  if (

    raw === "4" ||

    raw === "Q4" ||

    raw === "quest-4"

  ) {

    return true;

  }



  if (

    raw === "5" ||

    raw === "Q5" ||

    raw === "quest-5"

  ) {

    return true;

  }



  const numericQuestId =

    Number(raw);



  return (

    Number.isFinite(

      numericQuestId

    ) &&

    numericQuestId >= 2

  );

}



function normalizeStatus(

  value

) {

  return String(

    value || ""

  )

    .trim()

    .toLowerCase();

}



function normalizePlanCode(

  value

) {

  return String(

    value || ""

  )

    .trim()

    .toLowerCase();

}



function isFutureDate(

  value

) {

  if (!value) {

    return false;

  }



  const time =

    new Date(

      value

    ).getTime();



  return (

    Number.isFinite(

      time

    ) &&

    time > Date.now()

  );

}



/*

 * ------------------------------------------------------------

 * SUBSCRIPTION ACCESS HELPERS

 * ------------------------------------------------------------

 */



export function getSubscriptionAccessEnd(

  subscription

) {

  if (!subscription) {

    return null;

  }



  if (

    subscription.status ===

      "cancelled" &&

    subscription.cancel_at_period_end ===

      true

  ) {

    return (

      subscription.access_ends_at ||

      subscription.current_period_end ||

      null

    );

  }



  return (

    subscription.current_period_end ||

    subscription.access_ends_at ||

    null

  );

}



function hasNormalSubscriptionAccess(

  subscription

) {

  if (!subscription) {

    return false;

  }



  const accessEndsAt =

    getSubscriptionAccessEnd(

      subscription

    );



  const hasFutureAccess =

    isFutureDate(

      accessEndsAt

    );



  const status =

    normalizeStatus(

      subscription.status

    );



  const isActiveStatus =

    status === "active";



  const isCancelledStatus =

    status === "cancelled";



  const isPastDueStatus =

    status === "past_due";



  return (

    (

      isActiveStatus &&

      hasFutureAccess

    ) ||

    (

      isPastDueStatus &&

      hasFutureAccess

    ) ||

    (

      isCancelledStatus &&

      hasFutureAccess

    )

  );

}



function hasManualUnlimitedAccess(

  subscription

) {

  if (!subscription) {

    return false;

  }



  const status =

    normalizeStatus(

      subscription.status

    );



  const planCode =

    normalizePlanCode(

      subscription.plan_code

    );



  const accessEndsAt =

    getSubscriptionAccessEnd(

      subscription

    );



  const isActive =

    status === "active" ||

    status === "trialing";



  const accessStillValid =

    !accessEndsAt ||

    isFutureDate(

      accessEndsAt

    );



  return (

    planCode ===

      "gwin_unlimited" &&

    isActive &&

    accessStillValid

  );

}



/*

 * ------------------------------------------------------------

 * RESOURCE ACCESS

 * ------------------------------------------------------------

 *

 * Keep the older API shape for compatibility.

 *

 * Referral access may still be supplied by another caller

 * using paidJourneyAccess.js.

 *

 * subscriptions.js itself no longer requests it.

 */

export function userCanAccessQuestResource(

  resource,

  subscriptionStatus

) {

  const requiredQuest =

    Number(

      resource?.requiredQuest ||

        resource?.questId ||

        resource?.quest_id ||

        1

    );



  if (

    !Number.isFinite(

      requiredQuest

    ) ||

    requiredQuest <= 1

  ) {

    return true;

  }



  return (

    subscriptionStatus

      ?.isSubscribed ===

      true ||

    subscriptionStatus

      ?.hasReferralPaid50Access ===

      true ||

    subscriptionStatus

      ?.hasPaidJourneyAccess ===

      true

  );

}



/*

 * ------------------------------------------------------------

 * CURRENT USER SUBSCRIPTION STATUS

 * ------------------------------------------------------------

 *

 * IMPORTANT:

 *

 * This function no longer:

 *

 * - calls auth.getUser()

 * - checks referral access

 * - checks once-off journey access

 *

 * It only checks the old/manual subscription record.

 */

export async function getMySubscriptionStatus() {

  /*

   * getSession() normally reads the existing local

   * Supabase session instead of performing another

   * /auth/v1/user verification request.

   */

  const {

    data: {

      session,

    },

    error: sessionError,

  } =

    await supabase.auth.getSession();



  if (sessionError) {

    console.warn(

      "Could not read subscription auth session:",

      sessionError

    );

  }



  const user =

    session?.user ||

    null;



  if (!user) {

    return {

      loading: false,



      isSubscribed: false,



      hasSubscriptionAccess:

        false,



      /*

       * Keep these properties for older callers.

       *

       * Referral/once-off access is now resolved

       * through paidJourneyAccess.js instead.

       */

      hasReferralPaid50Access:

        false,



      hasPaidJourneyAccess:

        false,



      accessSource: null,



      subscription: null,



      accessEndsAt: null,



      cancelAtPeriodEnd:

        false,



      isCancelledButStillActive:

        false,



      isExpired: false,



      subscriptionsEnabled:

        SUBSCRIPTIONS_ENABLED,

    };

  }



  const {

    data,

    error,

  } =

    await supabase

      .from(

        "user_subscriptions"

      )

      .select(

        [

          "status",

          "plan_code",

          "plan_name",

          "amount",

          "currency",

          "payfast_token",

          "current_period_start",

          "current_period_end",

          "cancel_at_period_end",

          "cancelled_at",

          "access_ends_at",

          "expired_at",

          "last_payment_status",

          "last_payment_at",

        ].join(", ")

      )

      .eq(

        "user_id",

        user.id

      )

      .maybeSingle();



  if (error) {

    console.warn(

      "Could not load subscription:",

      error

    );



    return {

      loading: false,



      isSubscribed: false,



      hasSubscriptionAccess:

        false,



      hasReferralPaid50Access:

        false,



      hasPaidJourneyAccess:

        false,



      accessSource: null,



      subscription: null,



      accessEndsAt: null,



      cancelAtPeriodEnd:

        false,



      isCancelledButStillActive:

        false,



      isExpired: false,



      subscriptionsEnabled:

        SUBSCRIPTIONS_ENABLED,

    };

  }



  const accessEndsAt =

    getSubscriptionAccessEnd(

      data

    );



  /*

   * When subscriptions are enabled:

   *

   * use the normal monthly subscription.

   *

   * When subscriptions are disabled:

   *

   * only allow the old manually assigned

   * gwin_unlimited plan.

   */

  const hasSubscriptionAccess =

    SUBSCRIPTIONS_ENABLED

      ? hasNormalSubscriptionAccess(

          data

        )

      : hasManualUnlimitedAccess(

          data

        );



  const accessSource =

    hasSubscriptionAccess

      ? SUBSCRIPTIONS_ENABLED

        ? "subscription"

        : "manual_unlimited"

      : null;



  const isCancelledButStillActive =

    SUBSCRIPTIONS_ENABLED &&

    normalizeStatus(

      data?.status

    ) === "cancelled" &&

    data?.cancel_at_period_end ===

      true &&

    isFutureDate(

      accessEndsAt

    );



  return {

    loading: false,



    /*

     * Keep this property for old code.

     *

     * It now strictly represents subscription/

     * manual-unlimited access from this helper.

     */

    isSubscribed:

      hasSubscriptionAccess,



    hasSubscriptionAccess,



    /*

     * Referral/once-off access is intentionally

     * NOT queried by subscriptions.js.

     */

    hasReferralPaid50Access:

      false,



    hasPaidJourneyAccess:

      hasSubscriptionAccess,



    accessSource,



    subscription:

      data || null,



    accessEndsAt,



    cancelAtPeriodEnd:

      data

        ?.cancel_at_period_end ===

      true,



    isCancelledButStillActive,



    isExpired:

      !!data &&

      !hasSubscriptionAccess,



    subscriptionsEnabled:

      SUBSCRIPTIONS_ENABLED,

  };

}



/*

 * ------------------------------------------------------------

 * SUBSCRIPTION CHECKOUT

 * ------------------------------------------------------------

 */



export async function startPayFastCheckout() {

  if (

    !SUBSCRIPTIONS_ENABLED

  ) {

    throw new Error(

      "Subscription checkout is currently disabled."

    );

  }



  const {

    data,

    error,

  } =

    await supabase.functions.invoke(

      "create-payfast-checkout",

      {

        body: {

          planCode:

            "gwin_monthly",

        },

      }

    );



  if (error) {

    throw error;

  }



  if (

    !data?.processUrl ||

    !data?.fields

  ) {

    throw new Error(

      "Invalid PayFast checkout response"

    );

  }



  const form =

    document.createElement(

      "form"

    );



  form.method =

    "POST";



  form.action =

    data.processUrl;



  form.style.display =

    "none";



  Object.entries(

    data.fields

  ).forEach(

    ([

      key,

      value,

    ]) => {

      const input =

        document.createElement(

          "input"

        );



      input.type =

        "hidden";



      input.name =

        key;



      input.value =

        String(

          value ??

            ""

        );



      form.appendChild(

        input

      );

    }

  );



  document.body.appendChild(

    form

  );



  form.submit();

}



/*

 * ------------------------------------------------------------

 * SUBSCRIPTION CANCELLATION

 * ------------------------------------------------------------

 */



export async function cancelMySubscriptionAtPeriodEnd() {

  if (

    !SUBSCRIPTIONS_ENABLED

  ) {

    throw new Error(

      "Subscription cancellation is currently disabled."

    );

  }



  const {

    data,

    error,

  } =

    await supabase.functions.invoke(

      "cancel-payfast-subscription",

      {

        body: {},

      }

    );



  if (error) {

    throw error;

  }



  return data;

}



/*

 * ------------------------------------------------------------

 * BILLING HISTORY

 * ------------------------------------------------------------

 */



export async function getMyBillingHistory() {

  const {

    data,

    error,

  } =

    await supabase

      .from(

        "subscription_payments"

      )

      .select(

        "id, m_payment_id, pf_payment_id, payment_status, amount, currency, plan_code, billing_date, created_at"

      )

      .eq(

        "payment_status",

        "COMPLETE"

      )

      .order(

        "billing_date",

        {

          ascending:

            false,



          nullsFirst:

            false,

        }

      )

      .order(

        "created_at",

        {

          ascending:

            false,

        }

      );



  if (error) {

    throw error;

  }



  return (

    data ||

    []

  );

}



export function delay(

  ms

) {

  return new Promise(

    (resolve) =>

      setTimeout(

        resolve,

        ms

      )

  );

}



/*

 * ------------------------------------------------------------

 * WAIT FOR ACTIVE SUBSCRIPTION

 * ------------------------------------------------------------

 *

 * This only waits for subscription/manual-unlimited access.

 *

 * Referral and once-off purchase polling belongs to

 * paidJourneyAccess / onceOffPayments instead.

 */

export async function waitForActiveSubscription({

  attempts = 12,

  delayMs = 2500,

} = {}) {

  for (

    let i = 0;

    i < attempts;

    i += 1

  ) {

    const result =

      await getMySubscriptionStatus();



    if (

      result

        .hasSubscriptionAccess ===

      true

    ) {

      return result;

    }



    await delay(

      delayMs

    );

  }



  return {

    loading: false,



    isSubscribed:

      false,



    hasSubscriptionAccess:

      false,



    hasReferralPaid50Access:

      false,



    hasPaidJourneyAccess:

      false,



    accessSource:

      null,



    subscription:

      null,



    accessEndsAt:

      null,



    cancelAtPeriodEnd:

      false,



    isCancelledButStillActive:

      false,



    isExpired:

      false,



    subscriptionsEnabled:

      SUBSCRIPTIONS_ENABLED,

  };

}